import React from 'react'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import FormControl from '@mui/material/FormControl'
import Select, { SelectChangeEvent, selectClasses } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { CurrencyInput } from './CurrencyInput'
import { FieldHookConfig, useField } from 'formik'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import { isInvalid } from './utils'
import { NumericInput } from './NumericInput'
import { DiscountType } from '@cbgms/api/modules/enums'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'

interface DiscountInputProps<Value>
  extends Omit<React.ComponentProps<typeof NumericInput>, 'component' | 'defaultChecked' | 'defaultValue' | 'name' | 'value'>,
    Pick<FieldHookConfig<Value>, 'name' | 'validate'> {
  selectName: string
}

interface IDiscountSelect {
  name: string
  disabled?: boolean
  onChange: (value: DiscountType) => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'center',
      alignItems: 'center'
    },
    input: {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderRight: 'none'
    },
    selectOutline: {
      height: theme.spacing(5),
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    },
    select: {
      [`& .${selectClasses.outlined}.${selectClasses.outlined}`]: {
        width: 40,
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(2)
      }
    },
    selectIcon: {
      display: 'none'
    }
  })
)

const DiscountSelect: React.FC<IDiscountSelect> = ({ disabled, name, onChange }) => {
  const classes = useStyles()
  const [input, meta, helpers] = useField({ name })
  useRegisterFormField(name)
  const { currencySymbol } = React.useContext(LocaleContext)

  const options = [
    { key: DiscountType.Percentage, text: '%', value: DiscountType.Percentage },
    { key: DiscountType.Amount, text: currencySymbol, value: DiscountType.Amount }
  ]

  const changeHandler = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as DiscountType
    helpers.setValue(value)
    onChange(value)
  }

  return (
    <FormControl variant='outlined' className={classes.select} disabled={disabled}>
      <Select
        id={input.name}
        error={isInvalid(meta)}
        {...input}
        value={input.value ? input.value : options[0].value}
        className={classes.selectOutline}
        name={name}
        onChange={changeHandler}
        classes={{ icon: classes.selectIcon }}
      >
        {options.map(o => (
          <MenuItem value={o.value} key={o.key}>
            {o.text}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export function DiscountInput<Value>({ name, selectName, disabled, ...rest }: DiscountInputProps<Value>) {
  const classes = useStyles()
  const [discountType, setDiscountType] = React.useState(DiscountType.Percentage)

  const handleSelectChange = (value: DiscountType) => setDiscountType(value)

  const InputComponent = () => {
    if (discountType === DiscountType.Amount) {
      return (
        <CurrencyInput
          name={name}
          InputProps={{
            classes: {
              notchedOutline: classes.input
            }
          }}
          {...rest}
        />
      )
    }
    return (
      <NumericInput
        name={name}
        InputProps={{
          classes: {
            notchedOutline: classes.input
          }
        }}
        className={classes.input}
        {...rest}
      />
    )
  }

  return (
    <div className={classes.container}>
      <InputComponent />
      <DiscountSelect name={selectName} disabled={disabled} onChange={handleSelectChange} />
    </div>
  )
}
